import React, { useEffect } from 'react';
import { Pressable } from 'react-native';

import { Box, Inline } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import { Absolute, BodyText } from '../../../ui';
import ProProfileLazyImage from '../../../ui/LazyImage/ProProfileLazyImage';

import formatProfilePhoto from '../../../../modules/formatters/formatProfilePhoto';
import analytics from '../../../../modules/analytics';

import type { ProfileGalleryImage } from '../../../../api/Providers/GalleryImages';

export type ProfileDesktopImageGridProps = {
  galleryImages: ProfileGalleryImage[];
  inNewDesktopBreakpoint: boolean;
  onSeeAllPhotosClick: () => void;
  onImageClick: (index: number) => void;
  providerId?: number;
};

export const ProfileDesktopImageGrid = ({
  galleryImages,
  inNewDesktopBreakpoint,
  onSeeAllPhotosClick,
  onImageClick,
  providerId,
}: ProfileDesktopImageGridProps) => {
  useEffect(() => {
    analytics.track('client_revamp_proprofile_desktop_gallery_view', {
      profile_type: 'provider',
      cover_image_click: false,
    });
  }, []);

  const placeHolderGalleryArrary = () => {
    if (galleryImages.length <= 6) {
      return ([
        ...Array(
          galleryImages.length < 3
            ? 4
            : 6 - galleryImages.length,
        ),
      ]);
    }
    return null;
  };

  return (
    <Inline
      space={8}
      maxWidth={930}
      width="100%"
      style={{
        aspectRatio: '3 / 1',
      }}
      testID="revamp_desktop_gallery"
    >
      {/* 2 big images */}
      {galleryImages.map((image, index) => (
        (index < 2) && (
          <ProProfileLazyImage
            source={formatProfilePhoto(image.url, '_300x300.jpg')}
            fallback={image.url}
            description={image.blurb || `galleryImage-${index}`}
            width="100%"
            height="100%"
            dataSet={{
              event: `galleryImage-${index}`,
            }}
            styles={{
              width: 'calc(33.33% - 5.33px)',
            }}
            inNewDesktopBreakpoint={inNewDesktopBreakpoint}
            onPress={() => onImageClick(index)}
            borderRadius={4}
            testID={`revamp_desktop_gallery_large_image_${index}`}
            key={`revamp_desktop_gallery_large_image_${index + 1}_providerId_${providerId}`}
          />
        )
      ))}
      {/* show gray box when no photo */}
      {(galleryImages.length < 2) && (
        <ProProfileLazyImage
          testID="revamp_desktop_gallery_large_image_empty"
          description="empty-image"
          width="100%"
          height="100%"
          dataSet={{
            event: 'emty-galleryImage',
          }}
          showPlaceholder
          placeholderColor={BrandColor.LightGrey}
          styles={{
            width: 'calc(33.33% - 5.33px)',
          }}
          inNewDesktopBreakpoint={inNewDesktopBreakpoint}
          borderRadius={4}
        />
      )}
      {/* desktop view small photos */}
      <Inline
        style={{
          width: 'calc(33.33% - 5.33px)',
          height: 'auto',
          alignContent: 'space-between',
        }}
        justifyContent="space-between"
        flexWrap="wrap"
      >
        {galleryImages.map((image, index) => (
          (index > 1 && index < 6) && (
            <ProProfileLazyImage
              testID={`revamp_desktop_gallery_small_image_${index}`}
              source={formatProfilePhoto(image.url, '_300x300.jpg')}
              description={image.blurb || `galleryImage-${index}`}
              fallback={image.url}
              width={126.3}
              height={127}
              dataSet={{
                event: `galleryImage-${index}`,
              }}
              borderRadius={4}
              inNewDesktopBreakpoint={inNewDesktopBreakpoint}
              onPress={() => onImageClick(index)}
              isSmallImage
              styles={{
                width: 'calc(50% - 4px)',
                height: 'calc(50% - 4px)',
              }}
              key={`revamp_desktop_gallery_small_image_${index + 1}_providerId_${providerId}`}
            />
          )
        ))}
        {galleryImages.length < 7
          && placeHolderGalleryArrary().map(index => (
            <ProProfileLazyImage
              testID={`revamp_desktop_gallery_small_image_empty_${index}`}
              description={`empty-small-image-${index}`}
              dataSet={{
                event: `galleryEmptyImage-${index}`,
              }}
              width="100%"
              height="100%"
              showPlaceholder
              placeholderColor={BrandColor.LightGrey}
              borderRadius={4}
              inNewDesktopBreakpoint={inNewDesktopBreakpoint}
              isSmallImage
              styles={{
                width: 'calc(50% - 4px)',
                height: 'calc(50% - 4px)',
              }}
              key={`revamp_desktop_gallery_small_image_empty_${index + 1}_providerId_${providerId}`}

            />
          ))}
        {/* show the see all button */}
        {(galleryImages.length > 6) && (
          <Absolute
            testID="revamp_desktop_gallery_see_all_button"
            bottom="5.5%"
            right="25%"
            style={{
              transform: 'translateX(50%)',
            }}
          >
            <Pressable
              onPress={onSeeAllPhotosClick}
            >
              <Box
                color={BrandColor.BackInBlack}
                borderRadius={4}
                paddingX={8}
                paddingY={4}
                width={96}
                height={20}
              >
                <BodyText
                  color={BrandColor.WhiteWash}
                  appearance="smaller"
                >
                  Show All Photos
                </BodyText>
              </Box>
            </Pressable>
          </Absolute>
        )}
      </Inline>
    </Inline>
  );
};
